import { KEM_BACKEND_API_URL } from "@ch/api";
import { handleError } from "@ch/helpers/utils/handleError";

// Added dynamic import because of Vite build error
const handleAuthError = async () => {
  const { default: handleKemAuthError } = await import("./handleKemAuthError");
  handleKemAuthError();
};

export interface TPostChatStreamOptions {
  body: Record<string, unknown>;
  headers: Record<string, string>;
  onLoading?: (state: "sending" | "streaming" | undefined) => void;
  onUpdateChunk: (chunk: string) => void;
  onSetMessageId?: (newId: string) => void;
  onSetAudioUrl?: (url: string) => void;
  onSetRemainingTokens?: (remainingTokens: number) => void;
  onComplete?: () => void;
  suppressErrors?: boolean;
}

export type TCancel = () => void;

const postChatStream = (opts: TPostChatStreamOptions): TCancel => {
  const controller = new AbortController();
  const decoder = new TextDecoder();
  let pendingInfographic = ""; // Store infographic to append after stream completes

  opts.onLoading?.("sending");

  const handleStreamData = (chunkString: string) => {
    let processed = chunkString
      .replace(
        /data: ([\s\S]*?)(?=data: |event:|$)/g,
        (_match: string, content: string) => {
          const piece = content.replace(/\n\n$/, "");
          // Treat whitespace only chunks as a space.
          return piece.trim() === "" ? " " : piece;
        },
      )
      .replace(/event: response_complete/gm, "");

    const audioUrlRegex = /^output_audio_url:\s*(\S+\.(mp3|wav|m4a))/im;
    const kemMessageIdRegex = /^kem_message_id:\s*(.*)/im;
    const infographicRegex = /^infographic:.*?message=['"]([^'"]*)["']/im;

    const audioUrlMatch = audioUrlRegex.exec(processed);
    const kemMessageIdMatch = kemMessageIdRegex.exec(processed);
    const infographicMatch = infographicRegex.exec(processed);
    const remainingTokensMatch = /^remaining_tokens:\s*(.*)/im.exec(processed);

    if (audioUrlMatch) {
      const url = audioUrlMatch[1]?.trim();
      if (url) {
        opts.onSetAudioUrl?.(url);
      }
    }
    if (kemMessageIdMatch) {
      const newId = kemMessageIdMatch[1]?.trim();
      if (newId) {
        opts.onSetMessageId?.(newId);
      }
    }

    if (remainingTokensMatch) {
      const trimmedRemainingTokens = remainingTokensMatch[1]?.trim();
      if (
        trimmedRemainingTokens &&
        trimmedRemainingTokens !== "null" &&
        trimmedRemainingTokens !== "undefined"
      ) {
        opts.onSetRemainingTokens?.(Number(trimmedRemainingTokens));
      }
    }

    // Extract and store infographic markdown content (don't append yet!)
    if (infographicMatch && !pendingInfographic) {
      pendingInfographic =
        infographicMatch[1]?.replace(/\\n/g, "\n").trim() ?? "";
    }

    // Remove the entire infographic line completely
    processed = processed.replace(/^infographic:.*$/gim, "");

    // Remove other metadata fields (each on their own line)
    processed = processed.replace(
      /^(output_audio_url|message_id|kem_message_id|user_message_id|infographic_messageid|remaining_tokens):\s*.*$/gim,
      "",
    );

    // Remove any Python object notation patterns (key=value pairs)
    // This catches leaked metadata like "latest_height='199.04 CM' height_measurement_date='2024-03-24T19:31:15.622Z'"
    processed = processed.replace(
      /\b\w+_(?:id|enum|channel|name|url|time|seen|gender|date|bmi|bp|status|edd|location|plan|notes|duration|at|audio|preference|sugar|meal|type|cholesterol|weight|height|waist|circumference|goal|hbaic|psa|file|examination|condition|procedure|surgeries|prescription|calculation|rule|key|version)=[^\s]+/g,
      "",
    );

    // Remove ObjectId patterns
    processed = processed.replace(/ObjectId\([^)]+\)/g, "");

    // Remove datetime patterns
    processed = processed.replace(/datetime\.datetime\([^)]+\)/g, "");

    // Treat whitespace only chunks as a space.
    if (processed.length > 0) {
      opts.onUpdateChunk(processed);
    }
  };

  fetch(`${KEM_BACKEND_API_URL}/chat`, {
    headers: opts.headers,
    method: "POST",
    body: JSON.stringify(opts.body),
    signal: controller.signal,
  })
    .then(async (response) => {
      // handle 401 unauthorized - logout user
      if (response.status === 401) {
        await handleAuthError();
        throw new Error("Session expired, please login again");
      }
      if (!response.ok) {
        const bodyText = await response.text().catch(() => "");
        throw new Error(
          bodyText || `Request failed with status ${response.status}`,
        );
      }
      if (!response.body) throw new Error("No stream available");
      opts.onLoading?.("streaming");
      const reader = response.body.getReader();
      const pump = (): Promise<void> =>
        reader.read().then(async ({ done, value }) => {
          if (done) {
            // Append infographic at the end if it exists
            if (pendingInfographic) {
              opts.onUpdateChunk(`\n\n${pendingInfographic}`);
            }
            opts.onLoading?.(undefined);
            opts.onComplete?.();
            return Promise.resolve();
          }
          const chunkString = decoder.decode(value);
          // handle invalid token in stream - logout user
          if (chunkString === '{"detail":"Invalid token"}') {
            await handleAuthError();
            throw new Error("Session expired, please login again");
          }
          if (chunkString.startsWith('{"detail":[{"type":')) {
            throw new Error(chunkString);
          }
          handleStreamData(chunkString);
          return pump();
        });
      return pump();
    })
    .catch((error) => {
      if (!opts.suppressErrors) {
        handleError(error, undefined, true);
      }
      opts.onLoading?.(undefined);
    });

  return () => {
    controller.abort();
    opts.onLoading?.(undefined);
  };
};

export default postChatStream;
